import React, { useState } from 'react'
import * as StringFormatter from '../../../global/formatters/string-formatter.js'
import DOMPurify from 'dompurify'
import { getRelativeBasePath } from '../../../global/environments/path.js'
import { DownloadFactsheet } from './DownloadFactsheet.jsx'
import { removeSpecialCharacters } from '../../utility/index.js'
import {
  MUTUAL_FUND_PATH,
  ETF_PATH,
  MODEL_PORTFOLIOS_PATH,
  FUTURE_SCHOLAR_529_PATH,
  SEPARATELY_MANAGED_ACCOUNTS_PATH,
  VARIABLE_PATH,
  INTERVAL_FUND_PATH,
  CLOSED_END_PATH,
} from '../../../components/FundDetails/Helpers/FundTypes.js'

export function getFundName(product, fund) {
  switch (product) {
    case ETF_PATH:
    case MUTUAL_FUND_PATH:
    case CLOSED_END_PATH:
    case MODEL_PORTFOLIOS_PATH:
    case SEPARATELY_MANAGED_ACCOUNTS_PATH:
    case INTERVAL_FUND_PATH:
      return fund.fundName
    case VARIABLE_PATH:
    case FUTURE_SCHOLAR_529_PATH:
      return fund.name
    default:
      return ''
  }
}

export function getFundClass(product, section, fund) {
  switch (product) {
    case ETF_PATH:
      return 'ETF'
    case MUTUAL_FUND_PATH:
    case INTERVAL_FUND_PATH:
      return `${section === '#capital-gains' ? fund.className : fund.fundClass}`
    case VARIABLE_PATH:
      return fund.className?.trim() === '' ? 'Shares' : `${fund.className}`
    case CLOSED_END_PATH:
    case SEPARATELY_MANAGED_ACCOUNTS_PATH:
    case MODEL_PORTFOLIOS_PATH:
      return 'None'
    case FUTURE_SCHOLAR_529_PATH:
      return `${fund.className}`
    default:
      return ''
  }
}

export function getFundTitle(product, fund) {
  switch (product) {
    case ETF_PATH:
      return fund.symbol
    case MUTUAL_FUND_PATH:
    case CLOSED_END_PATH:
    case MODEL_PORTFOLIOS_PATH:
    case SEPARATELY_MANAGED_ACCOUNTS_PATH:
    case INTERVAL_FUND_PATH:
      return fund.fundName
    case VARIABLE_PATH:
    case FUTURE_SCHOLAR_529_PATH:
      return fund.name
    default:
      return ''
  }
}

export function getFundSubtitle(product, section, fund) {
  switch (product) {
    case ETF_PATH:
      return fund.fundName
    case MUTUAL_FUND_PATH:
    case INTERVAL_FUND_PATH:
      return `${fund.symbol} | ${section === '#capital-gains' ? fund.className : fund.fundClass}`
    case VARIABLE_PATH:
      return fund.className?.trim() === ''
        ? 'Class: Shares'
        : `Class: Class ${fund.className}`
    case CLOSED_END_PATH:
      return fund.symbol
    case FUTURE_SCHOLAR_529_PATH:
      return `Pricing Alternative: ${fund.className}`
    default:
      return ''
  }
}

export const MobileFundCard = ({
  fund,
  product,
  section,
  renderStars,
  displayPrimaryBenchmark,
  displayMorningstarAverage,
  asOfDates,
  getFixedDecimal,
  selectedPerformanceType529,
  selectedPerformanceType,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const isNav = selectedPerformanceType === 'nav'
  const isPuv = selectedPerformanceType529 === 'puv'

  let perfReturnLabel = ' with Maximum Sales Charge'
  if (product === INTERVAL_FUND_PATH) perfReturnLabel = ''
  else if (isNav) perfReturnLabel = ' at NAV'
  else if (product === ETF_PATH) perfReturnLabel = ' at Market'
  const perfAsOfDate =
    isNav || product === INTERVAL_FUND_PATH
      ? asOfDates?.navAsOfDate
      : asOfDates?.popAsOfDate

  const perf = {
    oneYear: isNav ? fund.navOneYear : fund.popOneYear,
    threeYear: isNav ? fund.navThreeYear : fund.popThreeYear,
    fiveYear: isNav ? fund.navFiveYear : fund.popFiveYear,
    tenYear: isNav ? fund.navTenYear : fund.popTenYear,
    sinceInception: isNav ? fund.navSinceInception : fund.popSinceInception,
    ytdPercent: isNav ? fund.ytdPercent : fund.ytdPercentPOP,
  }

  const perf529 = {
    oneYear: isPuv ? fund.puvOneYear : fund.popOneYear,
    threeYear: isPuv ? fund.puvThreeYear : fund.popThreeYear,
    fiveYear: isPuv ? fund.puvFiveYear : fund.popFiveYear,
    tenYear: isPuv ? fund.puvTenYear : fund.popTenYear,
    sinceInception: isPuv ? fund.puvSinceInception : fund.popSinceInception,
  }

  const cusipId =
    product === MODEL_PORTFOLIOS_PATH ||
    product === SEPARATELY_MANAGED_ACCOUNTS_PATH
      ? fund.accountID
      : fund.cusip

  return (
    <div
      className={`Accordion-item Card Card-body u-paddingVert2gu u-paddingHoriz0 u-marginBottom4gu u-bgColorSlateLight`}
    >
      <div className="u-paddingHoriz2gu">
        {section === '#age-based-portfolios' ? (
          <h2
            id={`${getFundName(product, fund)}-${getFundClass(product, section, fund)}-link`.replace(
              / /g,
              '-'
            )}
            className="u-textBold u-textSizeMsr u-textNoTransform"
          >
            Ages {fund.ageGroup}
          </h2>
        ) : (
          <h2 className="u-textSizeMsr u-textNoTransform">
            <a
              id={`${getFundName(product, fund)}-${getFundClass(product, section, fund)}-link`.replace(
                / /g,
                '-'
              )}
              className="u-textBold u-textColorBrandLight"
              href={DOMPurify.sanitize(
                getRelativeBasePath() +
                  `/investment-products/${product}/${StringFormatter.urlSafe(
                    removeSpecialCharacters(getFundName(product, fund))
                  )}/Class-${getFundClass(product, section, fund)}/details/?cusip=${cusipId}`
              )}
            >
              {getFundTitle(product, fund)}
            </a>
          </h2>
        )}
        <p className="u-textSizeMsrm1">
          {getFundSubtitle(product, section, fund)}
        </p>
        {section === '#prices-yields' && (
          <div className="u-marginTop2gu">
            <p>NAV $ : {fund.navDollar}</p>
            {product === ETF_PATH ? (
              <p>Market Price $ : {fund.marketPrice}</p>
            ) : (
              <p>POP $ : {fund.popDollar}</p>
            )}
            <p className="u-textColorSlateDark u-textSizeMsrm1">
              Price as of{' '}
              {StringFormatter.formatDateStandard(asOfDates?.priceAsOfDate)}
            </p>
          </div>
        )}
        {section === '#morningstar-ratings' && (
          <div className="u-marginTop2gu u-flex">
            {/* to-do Justify center */}
            <div className="u-paddingRight5gu">
              <p className="u-textNoWrap">Morningstar Rating</p>
              <p className="u-textColorSlateDark u-textSizeMsrm1">
                as of {StringFormatter.formatDateStandard(fund.asOfDate)}
              </p>
            </div>
            <div>
              <p>Overall</p>
              <div className="">
                {renderStars(fund.msOverallRating, fund.overallFunds, false)}
              </div>
            </div>
          </div>
        )}
        {(section === '#target-allocation-single-fund-portfolios' ||
          section === '#age-based-portfolios') && (
          <div className="u-marginTop2gu">
            <p>
              PUV $ as of {StringFormatter.formatDateStandard(fund.puvDate)}:
            </p>
            <p className="u-textColorSlateDark">{fund.puvVal}</p>
          </div>
        )}
        {section === CLOSED_END_PATH && (
          <div className="u-flex">
            <div className="u-marginRight4gu">
              <p>Annual Expenses</p>
              <p>{fund.annualExpense}</p>
            </div>
            <div>
              <p>Premium/Discount</p>
              <p>{fund.premiumByDiscount}</p>
            </div>
          </div>
        )}
      </div>
      <div className="u-flex u-flexAlignItemsCenter u-marginTop2gu u-paddingHoriz2gu">
        <button
          className="Button Button--icon Card--expandable u-marginRight2gu u-textColorWhite u-bgColorBrandLight"
          aria-expanded={isExpanded}
          aria-controls={`${getFundName(product, fund)}-${getFundClass(product, section, fund)}`.replace(
            / /g,
            '-'
          )}
          aria-label={`${isExpanded ? 'Hide' : 'Show'} Details for ${getFundName(product, fund)}`}
          onClick={() => setIsExpanded(!isExpanded)}
          data-analytics="cti-mobile-investment-products-expandable-btn"
        >
          <span aria-hidden="true" className="Icon--material u-textSizeMsr">
            {isExpanded ? 'remove' : 'add'}
          </span>
        </button>
        <p className="u-textColorBrandLight u-textSizeMsrm1" aria-hidden="true">
          {`View ${isExpanded ? 'Less' : ''} Details`}
        </p>
      </div>
      {isExpanded && (
        <div
          id={`${getFundName(product, fund)}-${getFundClass(product, section, fund)}`.replace(
            / /g,
            '-'
          )}
          role="region"
          aria-labelledby={`${getFundName(product, fund)}-${getFundClass(product, section, fund)}-link`.replace(
            / /g,
            '-'
          )}
        >
          {section === '#performance' && (
            <>
              <div className="u-borderTop u-marginTop4gu u-paddingTop4gu">
                <div className="u-paddingHoriz2gu">
                  {fund.categoryName === 'Money Market' && (
                    <div className="u-marginBottom2gu">
                      <p className="u-textSizeMsrm1 u-textNoWrap">
                        7-day Current Yield as of{' '}
                        {StringFormatter.formatDateStandard(
                          fund.sevenDayYldDate
                        )}
                        : {fund.sevenDayYldCurrent}
                      </p>
                      <p className="u-textSizeMsrm1 u-textNoWrap">
                        7-day Unsubsidized Yield as of{' '}
                        {StringFormatter.formatDateStandard(
                          fund.sevenDayYldUnsubDate
                        )}
                        : {fund.sevenDayYldUnsub}
                      </p>
                      <p className="u-textSizeMsrm1 u-textNoWrap">
                        7-day Effective Yield as of{' '}
                        {StringFormatter.formatDateStandard(
                          fund.sevenDayYldEffDate
                        )}
                        : {fund.sevenDayYldEff}
                      </p>
                    </div>
                  )}
                  <p className="u-textSizeMsrm1">YTD% : {perf.ytdPercent}</p>
                  <p className="u-textColorSlateDark u-textSizeMsrm1">
                    as of{' '}
                    {StringFormatter.formatDateStandard(asOfDates?.ytdAsOfDate)}
                  </p>
                </div>
                <p
                  id={`${getFundName(product, fund)}-perf-avg-returns`.replace(
                    / /g,
                    '-'
                  )}
                  className="u-marginTop2gu u-paddingHoriz2gu u-textColorBrandDark u-textBold u-textSizeMsrm1"
                >
                  {`Average Annual Total Returns (%)${perfReturnLabel} as of ${StringFormatter.formatDateStandard(perfAsOfDate)}`}
                </p>
                <table
                  className="Table MobileInvestmentProductTable"
                  aria-labelledby={`${getFundName(product, fund)}-perf-avg-returns`.replace(
                    / /g,
                    '-'
                  )}
                >
                  {(displayPrimaryBenchmark || displayMorningstarAverage) && (
                    <thead>
                      <tr className="Table-row">
                        <td></td>
                        <th
                          aria-label={getFundName(product, fund)}
                          scope="col"
                        ></th>
                        {displayPrimaryBenchmark && (
                          <th className="Table-cell u-textRight" scope="col">
                            {fund.benchmarkName}
                          </th>
                        )}
                        {displayMorningstarAverage && (
                          <th className="Table-cell u-textRight" scope="col">
                            {`Morningstar Category: ${fund.msCategoryName || fund.msCategory}`}
                          </th>
                        )}
                      </tr>
                    </thead>
                  )}
                  <tbody className="Table-body">
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        1 Year
                      </th>
                      <td className="Table-cell u-textRight">{perf.oneYear}</td>
                      {displayPrimaryBenchmark && (
                        <td className="Table-cell u-paddingRight2gu u-textRight">
                          {fund.benchmarkOneYear}
                        </td>
                      )}
                      {displayMorningstarAverage && (
                        <td className="Table-cell u-textRight">
                          {fund.msCategoryOneYear || '-'}
                        </td>
                      )}
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        3 Year
                      </th>
                      <td className="Table-cell u-textRight">
                        {perf.threeYear}
                      </td>
                      {displayPrimaryBenchmark && (
                        <td className="Table-cell u-paddingRight2gu u-textRight">
                          {fund.benchmarkThreeYear}
                        </td>
                      )}
                      {displayMorningstarAverage && (
                        <td className="Table-cell u-textRight">
                          {fund.msCategoryThreeYear || '-'}
                        </td>
                      )}
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        5 Year
                      </th>
                      <td className="Table-cell u-textRight">
                        {perf.fiveYear}
                      </td>
                      {displayPrimaryBenchmark && (
                        <td className="Table-cell u-paddingRight2gu u-textRight">
                          {fund.benchmarkFiveYear}
                        </td>
                      )}
                      {displayMorningstarAverage && (
                        <td className="Table-cell u-textRight">
                          {fund.msCategoryFiveYear || '-'}
                        </td>
                      )}
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        10 Year
                      </th>
                      <td className="Table-cell u-textRight">{perf.tenYear}</td>
                      {displayPrimaryBenchmark && (
                        <td className="Table-cell u-paddingRight2gu u-textRight">
                          {fund.benchmarkTenYear}
                        </td>
                      )}
                      {displayMorningstarAverage && (
                        <td className="Table-cell u-textRight">
                          {fund.msCategoryTenYear || '-'}
                        </td>
                      )}
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        Since Fund Incept*
                      </th>
                      <td className="Table-cell u-textRight">
                        {perf.sinceInception}
                      </td>
                      {displayPrimaryBenchmark && (
                        <td className="Table-cell u-textRight">
                          {fund.navSinceInception}
                        </td>
                      )}
                      {displayMorningstarAverage && (
                        <td className="Table-cell u-textRight">
                          {fund.navSinceInception}
                        </td>
                      )}
                    </tr>
                    <tr className="Table-row">
                      <th
                        className="Table-head"
                        scope="row"
                        colSpan={
                          displayPrimaryBenchmark && displayMorningstarAverage
                            ? 3
                            : displayPrimaryBenchmark ||
                                displayMorningstarAverage
                              ? 2
                              : 1
                        }
                      >
                        Fund Inception Date
                      </th>
                      <td className="Table-cell u-textRight">
                        {StringFormatter.formatDateStandard(fund.inceptionDt)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="u-borderTop u-marginTop4gu u-paddingTop4gu">
                <p
                  id={`${getFundName(product, fund)}-perf-expense-ratio`.replace(
                    / /g,
                    '-'
                  )}
                  className="u-paddingHoriz2gu u-textColorBrandDark u-textBold u-textSizeMsrm1"
                >
                  Expense Ratio as of{' '}
                  {StringFormatter.formatDateStandard(
                    asOfDates?.expenseAsOfDate
                  )}
                </p>
                <table
                  className="Table MobileInvestmentProductTable"
                  aria-labelledby={`${getFundName(product, fund)}-perf-expense-ratio`.replace(
                    / /g,
                    '-'
                  )}
                >
                  <tbody className="Table-body">
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        Gross
                      </th>
                      <td className="Table-cell u-textRight">
                        {fund.grossExp}
                      </td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        Net
                      </th>
                      <td className="Table-cell u-textRight">{fund.netExp}</td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        Waiver Expiration Date
                      </th>
                      <td className="Table-cell u-textRight">
                        {StringFormatter.formatDateStandard(
                          fund.expenseRatioWaiverDate
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <DownloadFactsheet fund={fund} mobileFundCard={true} />
            </>
          )}
          {section === '#prices-yields' && (
            <>
              <div className="u-borderTop u-marginTop4gu u-paddingTop4gu">
                {/* to-do Might need to get this value from fundRoot */}
                <p
                  id={`${getFundName(product, fund)}-prices-yields`.replace(
                    / /g,
                    '-'
                  )}
                  className="u-paddingHoriz2gu u-textColorBrandDark u-textBold u-textSizeMsrm1"
                >
                  Prices as of{' '}
                  {StringFormatter.formatDateStandard(asOfDates?.priceAsOfDate)}
                </p>
                <table
                  className="Table MobileInvestmentProductTable"
                  aria-labelledby={`${getFundName(product, fund)}-prices-yields`.replace(
                    / /g,
                    '-'
                  )}
                >
                  <tbody className="Table-body">
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        NAV $
                      </th>
                      <td className="Table-cell u-textRight">
                        {fund.navDollar}
                      </td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        POP $
                      </th>
                      <td className="Table-cell u-textRight">
                        {fund.popDollar}
                      </td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        {`${product === ETF_PATH ? 'Premium/Discount chg ($)' : 'NAV chg ($)'}`}
                      </th>
                      <td className="Table-cell u-textRight">
                        {product === ETF_PATH
                          ? fund.premiumDiscountAmount
                          : fund.navDollarChange}
                      </td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        {`${product === ETF_PATH ? 'Premium/Discount chg (%)' : 'NAV chg (%)'}`}
                      </th>
                      <td className="Table-cell u-textRight">
                        {product === ETF_PATH
                          ? fund.premiumDiscountPercent
                          : fund.navPercentageChange}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="u-borderTop u-marginTop4gu u-paddingTop4gu">
                <p
                  id={`${getFundName(product, fund)}-30-day-sec-yield`.replace(
                    / /g,
                    '-'
                  )}
                  className="u-paddingHoriz2gu u-textColorBrandDark u-textBold u-textSizeMsrm1"
                >
                  30-day SEC Yield as of{' '}
                  {StringFormatter.formatDateStandard(asOfDates?.secYldDate)}
                </p>
                <table
                  className="Table MobileInvestmentProductTable"
                  aria-labelledby={`${getFundName(product, fund)}-30-day-sec-yield`.replace(
                    / /g,
                    '-'
                  )}
                >
                  <tbody className="Table-body">
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        SEC Yield (%)
                      </th>
                      <td className="Table-cell u-textRight">
                        {fund.secYield}
                      </td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        Unsubsidized SEC Yield (%)
                      </th>
                      <td className="Table-cell u-textRight">
                        {fund.secYldUnsubsidized}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          )}
          {section === '#morningstar-ratings' && (
            <div className="u-borderTop u-marginTop4gu u-paddingTop4gu">
              <p
                id={`${getFundName(product, fund)}-morningstar-ratings`.replace(
                  / /g,
                  '-'
                )}
                className="u-paddingHoriz2gu u-textColorBrandDark u-textBold u-textSizeMsrm1"
              >
                Morningstar Category: {fund.msCategoryName}
              </p>
              <table
                aria-labelledby={`${getFundName(product, fund)}-morningstar-ratings`.replace(
                  / /g,
                  '-'
                )}
                className="Table MobileInvestmentProductTable"
              >
                <tbody className="Table-body">
                  <tr className="Table-row">
                    <th className="Table-head" scope="row">
                      Overall
                    </th>
                    <td className="Table-cell u-flex u-flexJustifyEnd">
                      {renderStars(fund.msOverallRating, fund.overallFunds)}
                    </td>
                  </tr>
                  <tr className="Table-row">
                    <th className="Table-head" scope="row">
                      3 Year
                    </th>
                    <td className="Table-cell u-flex u-flexJustifyEnd">
                      {renderStars(fund.threeYearRating, fund.threeYearFunds)}
                    </td>
                  </tr>
                  <tr className="Table-row">
                    <th className="Table-head" scope="row">
                      5 Year
                    </th>
                    <td className="Table-cell u-flex u-flexJustifyEnd">
                      {renderStars(fund.fiveYearRating, fund.fiveYearFunds)}
                    </td>
                  </tr>
                  <tr className="Table-row">
                    <th className="Table-head" scope="row">
                      10 Year
                    </th>
                    <td className="Table-cell u-flex u-flexJustifyEnd">
                      {renderStars(fund.tenYearRating, fund.tenYearFunds)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          {section === '#capital-gains' && (
            <>
              {fund.distributions?.length > 0 && (
                <div className="u-borderTop u-marginTop4gu u-paddingTop4gu">
                  <p
                    id={`${getFundName(product, fund)}-capital-gains-dist`.replace(
                      / /g,
                      '-'
                    )}
                    className="u-paddingHoriz2gu u-textColorBrandDark u-textBold u-textSizeMsrm1"
                  >
                    Breakdown of Capital Gains Distributions
                  </p>
                  <table
                    aria-labelledby={`${getFundName(product, fund)}-capital-gains-dist`.replace(
                      / /g,
                      '-'
                    )}
                    className="Table MobileInvestmentProductTable"
                  >
                    <tbody className="Table-body">
                      <tr className="Table-row">
                        <th className="Table-head" scope="row">
                          Record Date
                        </th>
                        <td className="Table-cell u-textRight">
                          {StringFormatter.formatDateStandard(
                            fund.distributions[0].recordDate
                          )}
                        </td>
                        {fund.distributions[1] && (
                          <td className="Table-cell u-textRight">
                            {StringFormatter.formatDateStandard(
                              fund.distributions[1].recordDate
                            )}
                          </td>
                        )}
                      </tr>
                      <tr className="Table-row">
                        <th className="Table-head" scope="row">
                          Ex Payable Date
                        </th>
                        <td className="Table-cell u-textRight">
                          {StringFormatter.formatDateStandard(
                            fund.distributions[0].exDivdndDate
                          )}
                        </td>
                        {fund.distributions[1] && (
                          <td className="Table-cell u-textRight">
                            {StringFormatter.formatDateStandard(
                              fund.distributions[1].exDivdndDate
                            )}
                          </td>
                        )}
                      </tr>
                      <tr className="Table-row">
                        <th className="Table-head" scope="row">
                          Short Term Capital Gains ($)
                        </th>
                        <td className="Table-cell u-textRight">
                          {fund.distributions[0].shortTermCapGain}
                        </td>
                        {fund.distributions[1] && (
                          <td className="Table-cell u-textRight">
                            {fund.distributions[1].shortTermCapGain}
                          </td>
                        )}
                      </tr>
                      <tr className="Table-row">
                        <th className="Table-head" scope="row">
                          Long Term Capital Gains ($)
                        </th>
                        <td className="Table-cell u-textRight">
                          {fund.distributions[0].longTermCapGain}
                        </td>
                        {fund.distributions[1] && (
                          <td className="Table-cell u-textRight">
                            {fund.distributions[1].longTermCapGain}
                          </td>
                        )}
                      </tr>
                      <tr className="Table-row">
                        <th className="Table-head" scope="row">
                          Total Capital Gains ($)
                        </th>
                        <td className="Table-cell u-textRight">
                          {fund.distributions[0].totalCapGain}
                        </td>
                        {fund.distributions[1] && (
                          <td className="Table-cell u-textRight">
                            {fund.distributions[1].totalCapGain}
                          </td>
                        )}
                      </tr>
                      <tr className="Table-row">
                        <th className="Table-head" scope="row">
                          % of Record Date NAV
                        </th>
                        <td className="Table-cell u-textRight">
                          {fund.distributions[0].totalCapGainPct}
                        </td>
                        {fund.distributions[1] && (
                          <td className="Table-cell u-textRight">
                            {fund.distributions[1].totalCapGainPct}
                          </td>
                        )}
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
              {fund.estimates && (
                <div className="u-borderTop u-marginTop4gu u-paddingTop4gu">
                  <p
                    id={`${getFundName(product, fund)}-capital-gains-estimates`.replace(
                      / /g,
                      '-'
                    )}
                    className="u-paddingHoriz2gu u-textColorBrandDark u-textBold u-textSizeMsrm1"
                  >
                    Breakdown of Capital Gains Estimates
                  </p>
                  <table
                    aria-labelledby={`${getFundName(product, fund)}-capital-gains-estimates`.replace(
                      / /g,
                      '-'
                    )}
                    className="Table MobileInvestmentProductTable"
                  >
                    <tbody className="Table-body">
                      <tr className="Table-row">
                        <th className="Table-head" scope="row">
                          Record Date
                        </th>
                        <td className="Table-cell u-textRight">
                          {StringFormatter.formatDateStandard(
                            fund.estimates.recordDate
                          )}
                        </td>
                      </tr>
                      <tr className="Table-row">
                        <th className="Table-head" scope="row">
                          Ex Payable Date
                        </th>
                        <td className="Table-cell u-textRight">
                          {StringFormatter.formatDateStandard(
                            fund.estimates.exDivdndDate
                          )}
                        </td>
                      </tr>
                      <tr className="Table-row">
                        <th className="Table-head" scope="row">
                          Short Term Capital Gains ($)
                        </th>
                        <td className="Table-cell u-textRight">{`${fund.estimates.shortTermCapGainSt} - ${fund.estimates.shortTermCapGainEnd}`}</td>
                      </tr>
                      <tr className="Table-row">
                        <th className="Table-head" scope="row">
                          Long Term Capital Gains ($)
                        </th>
                        <td className="Table-cell u-textRight">
                          {`${fund.estimates.longTermCapGainSt} - ${fund.estimates.longTermCapGainEnd}`}
                        </td>
                      </tr>
                      <tr className="Table-row">
                        <th className="Table-head" scope="row">
                          Total Capital Gains ($)
                        </th>
                        <td className="Table-cell u-textRight">{`${fund.estimates.totalCapGainSt} - ${fund.estimates.totalCapGainEnd}`}</td>
                      </tr>
                      <tr className="Table-row">
                        <th className="Table-head" scope="row">
                          % of Record Date NAV
                        </th>
                        <td className="Table-cell u-textRight">
                          {`${fund.estimates.totalCapGainPctSt} - ${fund.estimates.totalCapGainPctEnd}`}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
          {section === CLOSED_END_PATH && (
            <>
              <div className="u-borderTop u-marginTop4gu u-paddingTop4gu">
                <p
                  id={`${getFundName(product, fund)}-closed-end-prices`.replace(
                    / /g,
                    '-'
                  )}
                  className="u-paddingHoriz2gu u-textColorBrandDark u-textBold u-textSizeMsrm1"
                >
                  Prices as of{' '}
                  {StringFormatter.formatDateStandard(fund.priceAsOfDate)}
                </p>
                <table
                  aria-labelledby={`${getFundName(product, fund)}-closed-end-prices`.replace(
                    / /g,
                    '-'
                  )}
                  className="Table MobileInvestmentProductTable"
                >
                  <tbody className="Table-body">
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        NAV
                      </th>
                      <td className="Table-cell u-textRight">{fund.nav}</td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        MKT
                      </th>
                      <td className="Table-cell u-textRight">
                        {fund.mktValue}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="u-borderTop u-marginTop4gu u-paddingTop4gu">
                <p
                  id={`${getFundName(product, fund)}-closed-end-cumulative-returns`.replace(
                    / /g,
                    '-'
                  )}
                  className="u-paddingHoriz2gu u-textColorBrandDark u-textBold u-textSizeMsrm1"
                >
                  Cumulative Total Returns (%)
                </p>
                <table
                  aria-labelledby={`${getFundName(product, fund)}-closed-end-cumulative-returns`.replace(
                    / /g,
                    '-'
                  )}
                  className="Table MobileInvestmentProductTable"
                >
                  <thead>
                    <tr className="Table-row">
                      <td></td>
                      <th className="Table-cell u-textRight" scope="col">
                        NAV
                      </th>
                      <th className="Table-cell u-textRight" scope="col">
                        MKT
                      </th>
                    </tr>
                  </thead>
                  <tbody className="Table-body">
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        1 month
                      </th>
                      <td className="Table-cell u-textRight">
                        {fund.navOneMonth}
                      </td>
                      <td className="Table-cell u-textRight">
                        {fund.mktValueOneMonth}
                      </td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        3 month
                      </th>
                      <td className="Table-cell u-textRight">
                        {fund.navThreeMonth}
                      </td>
                      <td className="Table-cell u-textRight">
                        {fund.mktValueThreeMonth}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="u-borderTop u-marginTop4gu u-paddingTop4gu">
                <p
                  id={`${getFundName(product, fund)}-closed-end-avg-returns`.replace(
                    / /g,
                    '-'
                  )}
                  className="u-paddingHoriz2gu u-textColorBrandDark u-textBold u-textSizeMsrm1"
                >
                  Average Annual Total Returns (%)
                </p>
                <table
                  aria-labelledby={`${getFundName(product, fund)}-closed-end-avg-returns`.replace(
                    / /g,
                    '-'
                  )}
                  className="Table MobileInvestmentProductTable"
                >
                  <thead>
                    <tr className="Table-row">
                      <td></td>
                      <th className="Table-cell u-textRight" scope="col">
                        NAV
                      </th>
                      <th className="Table-cell u-textRight" scope="col">
                        MKT
                      </th>
                    </tr>
                  </thead>
                  <tbody className="Table-body">
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        1 Year
                      </th>
                      <td className="Table-cell u-textRight">
                        {fund.navOneYear}
                      </td>
                      <td className="Table-cell u-textRight">
                        {fund.mktValueOneYear}
                      </td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        3 Year
                      </th>
                      <td className="Table-cell u-textRight">
                        {fund.navThreeYear}
                      </td>
                      <td className="Table-cell u-textRight">
                        {fund.mktValueThreeYear}
                      </td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        5 Year
                      </th>
                      <td className="Table-cell u-textRight">
                        {fund.navFiveYear}
                      </td>
                      <td className="Table-cell u-textRight">
                        {fund.mktValueFiveYear}
                      </td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        10 Year
                      </th>
                      <td className="Table-cell u-textRight">
                        {fund.navTenYear}
                      </td>
                      <td className="Table-cell u-textRight">
                        {fund.mktValueTenYear}
                      </td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        Since Incept.*
                      </th>
                      <td className="Table-cell u-textRight">
                        {fund.navSinceInception}
                      </td>
                      <td className="Table-cell u-textRight">
                        {fund.mktValueSinceInception}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <DownloadFactsheet fund={fund} mobileFundCard={true} />
            </>
          )}
          {section === MODEL_PORTFOLIOS_PATH && (
            <>
              <div className="u-borderTop u-marginTop4gu u-paddingTop4gu">
                <div className="u-paddingHoriz2gu">
                  <p className="u-textSizeMsrm1">
                    YTD% Pure Gross: {fund.ytdPURE_GROSS}
                  </p>
                  <p className="u-textSizeMsrm1">YTD% Net: {fund.ytdNET}</p>
                  <p className="u-textColorSlateDark u-textSizeMsrm1">
                    as of {StringFormatter.formatDateStandard(fund.asOfDate)}
                  </p>
                </div>
                <p
                  id={`${getFundName(product, fund)}-model-portfolios-avg-returns`.replace(
                    / /g,
                    '-'
                  )}
                  className="u-marginTop2gu u-paddingHoriz2gu u-textColorBrandDark u-textBold u-textSizeMsrm1"
                >
                  Average Annual Returns as of{' '}
                  {StringFormatter.formatDateStandard(fund.asOfDate)}
                </p>
                <table
                  aria-labelledby={`${getFundName(product, fund)}-model-portfolios-avg-returns`.replace(
                    / /g,
                    '-'
                  )}
                  className="Table MobileInvestmentProductTable"
                >
                  <thead>
                    <tr className="Table-row">
                      <td></td>
                      <th className="Table-cell u-textRight" scope="col">
                        Pure Gross
                      </th>
                      <th className="Table-cell u-textRight" scope="col">
                        Net
                      </th>
                    </tr>
                  </thead>
                  <tbody className="Table-body">
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        YTD (cum.)
                      </th>
                      <td className="Table-cell u-textRight">
                        {getFixedDecimal(fund.ytdPURE_GROSS)}
                      </td>
                      <td className="Table-cell u-textRight">
                        {getFixedDecimal(fund.ytdNET)}
                      </td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        1 year
                      </th>
                      <td className="Table-cell u-textRight">
                        {getFixedDecimal(fund.oneYearPURE_GROSS)}
                      </td>
                      <td className="Table-cell u-textRight">
                        {getFixedDecimal(fund.oneYearNET)}
                      </td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        3 year
                      </th>
                      <td className="Table-cell u-textRight">
                        {getFixedDecimal(fund.threeYearPURE_GROSS)}
                      </td>
                      <td className="Table-cell u-textRight">
                        {getFixedDecimal(fund.threeYearNET)}
                      </td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        5 year
                      </th>
                      <td className="Table-cell u-textRight">
                        {getFixedDecimal(fund.fiveYearPURE_GROSS)}
                      </td>
                      <td className="Table-cell u-textRight">
                        {getFixedDecimal(fund.fiveYearNET)}
                      </td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        10 year
                      </th>
                      <td className="Table-cell u-textRight">
                        {getFixedDecimal(fund.tenYearPURE_GROSS)}
                      </td>
                      <td className="Table-cell u-textRight">
                        {getFixedDecimal(fund.tenYearNET)}
                      </td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        Since Fund Incept.*
                      </th>
                      <td className="Table-cell u-textRight">
                        {getFixedDecimal(fund.sinceIncepPURE_GROSS)}
                      </td>
                      <td className="Table-cell u-textRight">
                        {getFixedDecimal(fund.sinceIncepNET)}
                      </td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row" colSpan={2}>
                        Fund Inception Date
                      </th>
                      <td className="Table-cell u-textRight">
                        {StringFormatter.formatDateStandard(fund.inceptionDt)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <DownloadFactsheet fund={fund} mobileFundCard={true} />
            </>
          )}
          {section === SEPARATELY_MANAGED_ACCOUNTS_PATH && (
            <>
              <div className="u-borderTop u-marginTop4gu u-paddingTop4gu">
                <div className="u-paddingHoriz2gu">
                  <p className="u-textSizeMsrm1">
                    YTD% Pure Gross: {fund.ytdPercentGross}
                  </p>
                  <p className="u-textSizeMsrm1">
                    YTD% Net: {fund.ytdPercentNet}
                  </p>
                  <p className="u-textColorSlateDark u-textSizeMsrm1">
                    as of {StringFormatter.formatDateStandard(asOfDates)}
                  </p>
                </div>
                <p
                  id={`${getFundName(product, fund)}-managed-accounts-avg-returns`.replace(
                    / /g,
                    '-'
                  )}
                  className="u-marginTop2gu u-paddingHoriz2gu u-textColorBrandDark u-textBold u-textSizeMsrm1"
                >
                  Average Annual Returns (%) as of{' '}
                  {StringFormatter.formatDateStandard(asOfDates)}
                </p>
                <table
                  aria-labelledby={`${getFundName(product, fund)}-managed-accounts-avg-returns`.replace(
                    / /g,
                    '-'
                  )}
                  className="Table MobileInvestmentProductTable"
                >
                  <thead>
                    <tr className="Table-row">
                      <td></td>
                      <th className="Table-cell u-textRight" scope="col">
                        Pure Gross
                      </th>
                      <th className="Table-cell u-textRight" scope="col">
                        Net
                      </th>
                    </tr>
                  </thead>
                  <tbody className="Table-body">
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        YTD (cum.)
                      </th>
                      <td className="Table-cell u-textRight">
                        {getFixedDecimal(fund.ytdPercentGross)}
                      </td>
                      <td className="Table-cell u-textRight">
                        {getFixedDecimal(fund.ytdPercentNet)}
                      </td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        1 year
                      </th>
                      <td className="Table-cell u-textRight">
                        {getFixedDecimal(fund.grossOneYear)}
                      </td>
                      <td className="Table-cell u-textRight">
                        {getFixedDecimal(fund.netOneYear)}
                      </td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        3 year
                      </th>
                      <td className="Table-cell u-textRight">
                        {getFixedDecimal(fund.grossThreeYear)}
                      </td>
                      <td className="Table-cell u-textRight">
                        {getFixedDecimal(fund.netThreeYear)}
                      </td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        5 year
                      </th>
                      <td className="Table-cell u-textRight">
                        {getFixedDecimal(fund.grossFiveYear)}
                      </td>
                      <td className="Table-cell u-textRight">
                        {getFixedDecimal(fund.netFiveYear)}
                      </td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        10 year
                      </th>
                      <td className="Table-cell u-textRight">
                        {getFixedDecimal(fund.grossTenYear)}
                      </td>
                      <td className="Table-cell u-textRight">
                        {getFixedDecimal(fund.netTenYear)}
                      </td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        Since Fund Incept
                      </th>
                      <td className="Table-cell u-textRight">
                        {getFixedDecimal(fund.grossSinceInception)}
                      </td>
                      <td className="Table-cell u-textRight">
                        {getFixedDecimal(fund.netSinceInception)}
                      </td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row" colSpan={2}>
                        Fund Inception Date
                      </th>
                      <td className="Table-cell u-textRight">
                        {StringFormatter.formatDateStandard(fund.inceptionDt)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <DownloadFactsheet fund={fund} mobileFundCard={true} />
            </>
          )}
          {section === VARIABLE_PATH && (
            <>
              <div className="u-borderTop u-marginTop4gu u-paddingTop4gu">
                <p
                  id={`${getFundName(product, fund)}-vp-cumulative-total-returns`.replace(
                    / /g,
                    '-'
                  )}
                  className="u-paddingHoriz2gu u-textColorBrandDark u-textBold u-textSizeMsrm1"
                >
                  Cumulative Total Returns (%)
                </p>
                <table
                  aria-labelledby={`${getFundName(product, fund)}-vp-cumulative-total-returns`.replace(
                    / /g,
                    '-'
                  )}
                  className="Table MobileInvestmentProductTable"
                >
                  <tbody className="Table-body">
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        1 month
                      </th>
                      <td className="Table-cell u-textRight">
                        {fund.puvOneMonth}
                      </td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        3 month
                      </th>
                      <td className="Table-cell u-textRight">
                        {fund.puvThreeMonth}
                      </td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        YTD
                      </th>
                      <td className="Table-cell u-textRight">{fund.ytd}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="u-borderTop u-marginTop4gu u-paddingTop4gu">
                <p
                  id={`${getFundName(product, fund)}-vp-avg-returns`.replace(
                    / /g,
                    '-'
                  )}
                  className="u-paddingHoriz2gu u-textColorBrandDark u-textBold u-textSizeMsrm1"
                >
                  Average Annual Total Returns (%)
                </p>
                <table
                  aria-labelledby={`${getFundName(product, fund)}-vp-avg-returns`.replace(
                    / /g,
                    '-'
                  )}
                  className="Table MobileInvestmentProductTable"
                >
                  <tbody className="Table-body">
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        1 Year
                      </th>
                      <td className="Table-cell u-textRight">
                        {fund.puvOneYear}
                      </td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        3 Year
                      </th>
                      <td className="Table-cell u-textRight">
                        {fund.puvThreeYear}
                      </td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        5 Year
                      </th>
                      <td className="Table-cell u-textRight">
                        {fund.puvFiveYear}
                      </td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        10 Year
                      </th>
                      <td className="Table-cell u-textRight">
                        {fund.puvTenYear}
                      </td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        Since Incept.*
                      </th>
                      <td className="Table-cell u-textRight">
                        {fund.puvSinceInception}
                      </td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        Fund Inception Date
                      </th>
                      <td className="Table-cell u-textRight">
                        {StringFormatter.formatDateStandard(fund.inceptionDt)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="u-borderTop u-marginTop4gu u-paddingTop4gu">
                <p
                  id={`${getFundName(product, fund)}-vp-expense-ratio`.replace(
                    / /g,
                    '-'
                  )}
                  className="u-paddingHoriz2gu u-textColorBrandDark u-textBold u-textSizeMsrm1"
                >
                  Expense Ratio as of{' '}
                  {StringFormatter.formatDateStandard(
                    asOfDates?.expenseAsOfDate
                  )}
                </p>
                <table
                  aria-labelledby={`${getFundName(product, fund)}-vp-expense-ratio`.replace(
                    / /g,
                    '-'
                  )}
                  className="Table MobileInvestmentProductTable"
                >
                  <tbody className="Table-body">
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        Gross
                      </th>
                      <td className="Table-cell u-textRight">
                        {fund.grossExp}
                      </td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        Net
                      </th>
                      <td className="Table-cell u-textRight">{fund.netExp}</td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        Waiver Expiration Date
                      </th>
                      <td className="Table-cell u-textRight">
                        {fund.expenseRatioWaiverDate
                          ? StringFormatter.formatDateStandard(
                              fund.expenseRatioWaiverDate
                            )
                          : '-'}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          )}
          {(section === '#target-allocation-single-fund-portfolios' ||
            section === '#age-based-portfolios') && (
            <>
              <div className="u-borderTop u-marginTop4gu u-paddingTop4gu">
                <p className="u-paddingHoriz2gu u-marginBottom2gu u-textSizeMsrm1">
                  YTD % PUV $ as of{' '}
                  {StringFormatter.formatDateStandard(fund.ytdAsOfDate)}:{' '}
                  {fund.ytd}
                </p>
                <p
                  id={`${getFundName(product, fund)}-529-avg-returns`.replace(
                    / /g,
                    '-'
                  )}
                  className="u-paddingHoriz2gu u-textColorBrandDark u-textBold u-textSizeMsrm1"
                >
                  {`Average Annual Total Returns (%) as of ${StringFormatter.formatDateStandard(fund.asOfDate)}`}
                </p>
                <table
                  aria-labelledby={`${getFundName(product, fund)}-529-avg-returns`.replace(
                    / /g,
                    '-'
                  )}
                  className="Table MobileInvestmentProductTable"
                >
                  <tbody className="Table-body">
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        1 Year
                      </th>
                      <td className="Table-cell u-textRight">
                        {perf529.oneYear}
                      </td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        3 Year
                      </th>
                      <td className="Table-cell u-textRight">
                        {perf529.threeYear}
                      </td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        5 Year
                      </th>
                      <td className="Table-cell u-textRight">
                        {perf529.fiveYear}
                      </td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        10 Year
                      </th>
                      <td className="Table-cell u-textRight">
                        {perf529.tenYear}
                      </td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        Since Incept.*
                      </th>
                      <td className="Table-cell u-textRight">
                        {perf529.sinceInception}
                      </td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        Fund Inception Date
                      </th>
                      <td className="Table-cell u-textRight">
                        {StringFormatter.formatDateStandard(fund.inceptionDt)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="u-borderTop u-marginTop4gu u-paddingTop4gu">
                <p
                  id={`${getFundName(product, fund)}-529-expense-ratio`.replace(
                    / /g,
                    '-'
                  )}
                  className="u-paddingHoriz2gu u-textColorBrandDark u-textBold u-textSizeMsrm1"
                >
                  Expense Ratio as of{' '}
                  {StringFormatter.formatDateStandard(fund.expenseAsOfDate)}
                </p>
                <table
                  aria-labelledby={`${getFundName(product, fund)}-529-expense-ratio`.replace(
                    / /g,
                    '-'
                  )}
                  className="Table MobileInvestmentProductTable"
                >
                  <tbody className="Table-body">
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        Gross
                      </th>
                      <td className="Table-cell u-textRight">
                        {fund.grossExp}
                      </td>
                    </tr>
                    <tr className="Table-row">
                      <th className="Table-head" scope="row">
                        Net
                      </th>
                      <td className="Table-cell u-textRight">{fund.netExp}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          )}
          <div className="u-flex u-flexAlignItemsCenter u-marginTop2gu u-paddingHoriz2gu">
            <button
              className="Button Button--icon Card--expandable u-marginRight2gu u-textColorWhite u-bgColorBrandLight"
              aria-expanded={isExpanded}
              aria-controls={`${getFundName(product, fund)}-${getFundClass(product, section, fund)}`.replace(
                / /g,
                '-'
              )}
              aria-label={`${isExpanded ? 'Hide' : 'Show'} Details for ${getFundName(product, fund)}`}
              onClick={() => setIsExpanded(!isExpanded)}
              data-analytics="cti-mobile-investment-products-expandable-btn"
            >
              <span aria-hidden="true" className="Icon--material u-textSizeMsr">
                {isExpanded ? 'remove' : 'add'}
              </span>
            </button>
            <p
              className="u-textColorBrandLight u-textSizeMsrm1"
              aria-hidden="true"
            >
              {`View ${isExpanded ? 'Less' : ''} Details`}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
